import { colorTableOperationIcon } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Button, Checkbox, Flex } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox/Checkbox';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import * as Icon from 'react-feather';
import { useFormContext, UseFormSetValue, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { Modal, TextEllipsis } from '../../common/components';
import { TextLink } from '../../common/components/typography';
import {
  CreateTableRowButton,
  DeleteFormTableRowButton,
  DetailsTableRowButton,
  FormCheckbox,
  FormField,
  FormTableSequenceNumberField,
  getFormTableFieldName,
  getValidPeriodDisplayValueWithFormValueAttached,
  TableOperationButton,
  TanstackFormTable,
} from '../../common/form';
import { useUpdateSequenceNumbersRef } from '../../common/form/table/tanstack/useUpdateSequenceNumbersRef';
import {
  createCloneParam,
  isCloneParam,
  parseCloneParam,
} from '../../common/queryParams/clone';
import { CREATE_PARAM, isCreateParam } from '../../common/queryParams/create';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { getDatePeriodString } from '../../common/utils';
import { useSystemClassifierItem } from '../../configuration';
import { useDataObjectContext } from '../DataObjectContext';
import { usePanelParams } from '../usePanelParams';
import { DataObjectValidFormDataType, RequiredKeyDataType } from '../schema';
import translations from '../translations/KeysEditTable.translations.json';
import { useTableFilter } from '../useTableFilter';
import { KeyBulkEditPanel } from './KeyBulkEditPanel';
import { KeyEditPanel } from './KeyEditPanel';
import { RelatedAlgorithmsViewModal } from './RelatedAlgorithmsViewModal';
import { BulkEdit } from './types';
import { useKeyOptions } from './useKeyOptions';
import {
  createDefaultKey,
  KeyTableWrapper,
  renderClassifierPreview,
  renderClassifiersPreview,
} from './utils';

const { addTranslations, useTranslation } = i18nNsFactory('keys-form-table');
addTranslations(translations);
const columnHelper = createColumnHelper<RequiredKeyDataType>();

interface KeysBulkOperationButtonsProps {
  onDelete: () => void;
  onEdit: () => void;
}

function KeysBulkOperationButtons({
  onDelete,
  onEdit,
}: KeysBulkOperationButtonsProps) {
  const { t } = useTranslation();
  return (
    <>
      <Button id="bulk-edit-keys" onClick={onEdit}>
        {t('button.edit-keys')}
      </Button>
      <Button id="bulk-delete-keys" onClick={onDelete}>
        {t('button.delete-keys')}
      </Button>
    </>
  );
}

function getSelected(values: RequiredKeyDataType[]): number[] {
  return values
    .map((key, index) => [key.selected, index] as [boolean | undefined, number])
    .filter((kv) => kv[0])
    .map((kv) => kv[1]);
}

function KeyActionButtons({
  props,
}: {
  props: CellContext<RequiredKeyDataType, any>;
}) {
  const { t } = useTranslation();
  const hasRelatedAlgorithms =
    useWatch({
      name: `requiredKeys.${props.row.index}.relatedAlgorithmIds.length`,
    }) > 0;

  const [, setPanelParams] = usePanelParams();
  const relatedAlgorithmsParams = {
    requiredKeyAlgorithmsIndex: props.row.index,
  };
  const cloneRequiredKeyParams = {
    requiredKeyId: createCloneParam(props.row.index),
  };

  return (
    <Flex justify="center" gap="small">
      <DetailsTableRowButton
        detailsTitle={t('button.details')}
        emptyTitle={t('button.no-details')}
        context={props}
        hasDetails={hasRelatedAlgorithms}
        href={useHrefWithQueryParams(relatedAlgorithmsParams)}
        onClick={suppressHref(() => setPanelParams(relatedAlgorithmsParams))}
      />
      <TableOperationButton
        id={`key-clone-${props.row.index}`}
        title={t('button.clone')}
        icon={<Icon.Copy size={18} color={colorTableOperationIcon} />}
        type="text"
        href={useHrefWithQueryParams(cloneRequiredKeyParams)}
        onClick={suppressHref(() => setPanelParams(cloneRequiredKeyParams))}
        aria-label={t('button.clone')}
      />
      <DeleteFormTableRowButton context={props} />
    </Flex>
  );
}

function removeSelected(
  values: RequiredKeyDataType[],
  selected: Set<number>,
  setValue: UseFormSetValue<any>,
) {
  const key = values.find(
    (key, index) => selected.has(index) && key.relatedAlgorithmIds.length > 0,
  );
  if (key) {
    return { success: false, key: key.key };
  }

  const toBeRemovedIndexes = [...selected].toSorted((a, b) => b - a);
  const updated = toBeRemovedIndexes.reduce(
    (acc, index) => acc.toSpliced(index, 1),
    [...values],
  );
  setValue('requiredKeys', updated, { shouldDirty: true });

  return { success: true };
}

export const KeysEditTable: FC = () => {
  const { t } = useTranslation();
  const { setValue, getValues } = useFormContext<DataObjectValidFormDataType>();
  const [bulkEdit, setBulkEdit] = useState<BulkEdit | undefined>();
  const total =
    useWatch<DataObjectValidFormDataType, 'requiredKeys'>({
      name: 'requiredKeys',
    })?.length || 0;
  const indicatorA = useSystemClassifierItem('INDICATOR_STATUS', 'A')?.id;
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  const [{ requiredKeyId, requiredKeyIndex, ...panelParams }, setPanelParams] =
    usePanelParams();

  const { requiredKey, indicatorStatus, index, clone } = useMemo(() => {
    const indicatorStatus = getValues('indicatorStatus');
    let requiredKey: RequiredKeyDataType | undefined = undefined;
    let clone = false;
    let index: number | undefined = undefined;
    const requiredKeys = getValues('requiredKeys');

    if (requiredKeyId) {
      if (isCloneParam(requiredKeyId)) {
        index = parseCloneParam(requiredKeyId);
        requiredKey = {
          ...requiredKeys[index],
          id: null,
        };
        clone = true;
      } else if (isCreateParam(requiredKeyId)) {
        requiredKey = createDefaultKey({
          indicatorStatus,
          recipientIds: getValues('recipientIds') ?? [],
          sequenceNumber:
            Math.max(0, ...requiredKeys.map((key) => key.sequenceNumber)) + 1,
          statusId: indicatorA,
        });
      } else {
        requiredKey = requiredKeys.find(
          (key) => key.id === Number(requiredKeyId),
        );
        index = requiredKey && requiredKeys.indexOf(requiredKey);
      }
    } else if (!isNaN(Number(requiredKeyIndex))) {
      index = Number(requiredKeyIndex);
      requiredKey = requiredKeys[index];
    }

    return {
      requiredKey,
      indicatorStatus,
      index,
      clone,
    };
  }, [getValues, requiredKeyId, requiredKeyIndex, indicatorA]);

  const requiredKeyAlgorithmsIndex = Number(
    panelParams.requiredKeyAlgorithmsIndex,
  );
  const viewableRelatedAlgorithmsRequiredKey = useMemo(
    () =>
      isNaN(requiredKeyAlgorithmsIndex)
        ? undefined
        : getValues(`requiredKeys.${requiredKeyAlgorithmsIndex}`),
    [requiredKeyAlgorithmsIndex, getValues],
  );

  const { options, recipientClassifiers, ruleClassifiers, statusClassifiers } =
    useKeyOptions(indicatorStatus);
  const { setRequiredKeyFilters } = useDataObjectContext();
  const { columnFilters, setColumnFilters } = useTableFilter(
    setRequiredKeyFilters,
  );

  useEffect(() => {
    setSelected(new Set(getSelected(getValues('requiredKeys'))));
  }, [getValues, setSelected, total]);

  const deleteSelected = useCallback(() => {
    const performDelete = () => {
      const values = getValues('requiredKeys');
      const { success, key } = removeSelected(values, selected, setValue);
      if (success) {
        setSelected(new Set());
      } else {
        Modal.info({
          title: t('delete.title'),
          content: t('delete.disabled', { key }),
          onOk: Modal.destroyAll,
        });
      }
    };

    Modal.confirm({
      title: t('confirm.delete.title'),
      okText: t('confirm.delete.okText'),
      cancelText: t('confirm.delete.cancelText'),
      content: t('confirm.delete.content'),
      onOk: performDelete,
    });
  }, [getValues, selected, setValue, t]);

  const editSelected = useCallback(() => {
    setBulkEdit({ editable: selected, indicatorStatus });
  }, [indicatorStatus, selected]);

  const addRow = useCallback(() => {
    setPanelParams({ requiredKeyId: CREATE_PARAM });
  }, [setPanelParams]);

  const updateSequenceNumbers = useUpdateSequenceNumbersRef('requiredKeys');

  const submit = useCallback(
    (key: RequiredKeyDataType, clone = false) => {
      const opts = { shouldDirty: true };
      if (typeof index === 'undefined') {
        setValue(`requiredKeys`, getValues('requiredKeys').concat(key), opts);
      } else if (clone) {
        setValue(
          'requiredKeys',
          getValues('requiredKeys').toSpliced(index + 1, 0, {
            ...key,
            id: null,
          }),
          opts,
        );
        updateSequenceNumbers.current(index + 1);
      } else {
        setValue(`requiredKeys.${index}`, key, opts);
      }
      setPanelParams();
    },
    [index, setPanelParams, setValue, getValues, updateSequenceNumbers],
  );

  const onCheckAllChange = useCallback(
    (e: CheckboxChangeEvent) => {
      let selectedRows: number[] = [];
      const indexes = Array.from(Array(total).keys());
      const checked = e.target.checked;
      if (checked) {
        selectedRows = indexes;
      }
      indexes.forEach((_option, index) =>
        setValue(`requiredKeys.${index}.selected`, checked, {
          shouldDirty: true,
        }),
      );
      setSelected(new Set(selectedRows));
    },
    [setValue, total],
  );
  const onCheckChange = useCallback(
    (checked: boolean, index: number) => {
      setSelected((current) => {
        const newSelected = new Set(current);
        if (checked) {
          newSelected.add(index);
        } else {
          newSelected.delete(index);
        }
        return newSelected;
      });
      setValue(`requiredKeys.${index}.selected`, checked, {
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('selected', {
        header: () => (
          <Checkbox
            id="check-all-keys"
            indeterminate={selected.size > 0 && selected.size < total}
            checked={selected.size === total}
            onChange={onCheckAllChange}
          />
        ),
        cell: (props) => {
          const name = getFormTableFieldName(props);
          return (
            <FormFieldWrapper name={getFormTableFieldName(props)} size="small">
              <FormCheckbox
                name={name}
                onChange={(e) =>
                  onCheckChange(e.target.checked, props.row.index)
                }
              />
            </FormFieldWrapper>
          );
        },
        meta: { width: 32, className: 'sticky-left' },
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('sequenceNumber', {
        header: t('column.sequenceNumber.label'),
        cell: FormTableSequenceNumberField,
        meta: {
          width: 100,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('key', {
        header: t('column.key.label'),
        cell: KeyCell,
        meta: {
          minWidth: 520,
          width: 520,
        },
      }),
      columnHelper.accessor('name', {
        header: t('column.name.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <TextEllipsis>{value}</TextEllipsis>,
            }}
          />
        ),
        meta: {
          width: 150,
        },
      }),
      ...(indicatorStatus
        ? [
            columnHelper.accessor('statusId', {
              header: t('column.statusId.label'),
              cell: (props) => (
                <FormField
                  name={getFormTableFieldName(props)}
                  size="small"
                  preview={{
                    previewOnly: true,
                    render: (value) => (
                      <TextEllipsis>
                        {renderClassifierPreview(value, statusClassifiers)}
                      </TextEllipsis>
                    ),
                  }}
                />
              ),
              filterFn: 'includesOption',
              meta: {
                minWidth: 90,
                width: 90,
              },
            }),
          ]
        : []),
      columnHelper.accessor('ruleTypeId', {
        header: t('column.ruleTypeId.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => (
                <TextEllipsis>
                  {renderClassifierPreview(value, ruleClassifiers)}
                </TextEllipsis>
              ),
            }}
          />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: t('column.validPeriod.label'),
          cell: (props) => (
            <FormField
              name={getFormTableFieldName(props)}
              size="small"
              preview={{
                previewOnly: true,
                render: (value) => (
                  <TextEllipsis>
                    {getDatePeriodString(value?.[0], value?.[1])}
                  </TextEllipsis>
                ),
              }}
            />
          ),
          enableColumnFilter: true,
          filterFn: 'validPeriod',
          meta: {
            width: 140,
          },
        },
      ),
      columnHelper.accessor('voluntary', {
        header: t('column.voluntary.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => t(`column.voluntary.${value ? 'yes' : 'no'}`),
            }}
          ></FormField>
        ),
        filterFn: 'includesOption',
        meta: {
          minWidth: 112,
          width: 112,
        },
      }),
      columnHelper.accessor('recipientIds', {
        header: t('column.recipientIds.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) =>
                renderClassifiersPreview(value, recipientClassifiers),
            }}
          ></FormField>
        ),
        filterFn: 'includesOption',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description.label'),
        cell: (props) => (
          <FormField
            name={getFormTableFieldName(props)}
            size="small"
            preview={{
              previewOnly: true,
              render: (value) => <TextEllipsis>{value}</TextEllipsis>,
            }}
          />
        ),
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <CreateTableRowButton
              title={t('button.add-key')}
              onClick={addRow}
              autoFocusField="code"
            />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => <KeyActionButtons props={props} />,
        meta: {
          className: 'sticky-right',
          width: 120,
        },
      }),
    ],
    [
      addRow,
      indicatorStatus,
      onCheckAllChange,
      onCheckChange,
      recipientClassifiers,
      ruleClassifiers,
      selected.size,
      statusClassifiers,
      t,
      total,
    ],
  );

  const handlePanelClose = useCallback(() => {
    setPanelParams();
  }, [setPanelParams]);

  return (
    <KeyTableWrapper vertical align="end" gap="middle">
      <TanstackFormTable
        actions={
          selected.size ? (
            <KeysBulkOperationButtons
              onDelete={deleteSelected}
              onEdit={editSelected}
            />
          ) : undefined
        }
        name="requiredKeys"
        columns={columns}
        filters={columnFilters}
        onFilter={setColumnFilters}
        options={options}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {requiredKey && (
        <KeyEditPanel
          requiredKey={requiredKey}
          clone={clone}
          onClose={handlePanelClose}
          onSubmit={submit}
          indicatorStatus={indicatorStatus}
          index={index}
        />
      )}
      {viewableRelatedAlgorithmsRequiredKey && (
        <RelatedAlgorithmsViewModal
          requiredKey={viewableRelatedAlgorithmsRequiredKey}
          onClose={handlePanelClose}
          isEditMode={true}
        />
      )}
      {bulkEdit && (
        <KeyBulkEditPanel
          {...bulkEdit}
          onClose={() => setBulkEdit(undefined)}
        />
      )}
    </KeyTableWrapper>
  );
};

const FormFieldWrapper = styled(FormField)`
  padding-left: 8px;
`;

const KeyCell: FC<CellContext<RequiredKeyDataType, any>> = memo((props) => {
  const [, setPanelParams] = usePanelParams();
  const requiredKeyIndex = props.row.index;
  const href = useHrefWithQueryParams({ requiredKeyIndex });

  return (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: (value) => (
          <TextLink
            id={`edit-key-${props.row.index}`}
            href={href}
            onClick={suppressHref(() => {
              setPanelParams({ requiredKeyIndex });
            })}
          >
            <TextEllipsis>{value}</TextEllipsis>
          </TextLink>
        ),
      }}
    />
  );
});
